import configLib from '../../../configLib';
import { isPerfMetricsEnabled, measurePerf, recordPerfMetric } from '../../debug/performance';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { THROTTLE_OPTIONS } from '../../libSettings';
import { StockExchangeCodeType } from '../../models/fundamental/types';
import { parsingResponseFailed } from '../../store/gateway';
import { TradeDataCache } from '../../store/market-data/types';
import SubscriptionTimingTracker from '../../store-util/SubscriptionTimingTracker';
import { updateTradePriceCacheFromTrade } from '../../store-util/TradePriceCache';
import { parseGatewayData } from '../../util/GatewayHelpers';
import { dataArrayToMap } from '../../util/MarketDataHelpers';
import { WebSocketEventData } from '../custom-event/types';
import { ThrottleFuncSetup, throttleFunctionSetupForSymbolDataUpdates } from '../helpers';
import { LoggerService } from '../logger/Logger.service';
import { GatewayRequestType, GatewayResponseSubType, GatewayResponseType } from '../web-socket/types';

import { BaseSymbolSubscriptionService } from './BaseSymbolSubscription.service';
import { SymbolSubscriptionRequestType } from './types';

const LOG_PREFIX = '[MDST-SUBS]';
const TRADE_PRICE_CACHE_UPDATE_THROTTLE_MS = PredefinedMilisecondsTypeEnum.halfSecond;
const THROTTLE_NAME = 'trade-us';
const logger = LoggerService.getLogger();
/**
 * ace trades a single market (US), so one throttle bucket covers every trade response.
 * Trades arrive far faster than the UI can usefully repaint; coalescing them keeps
 * TradePriceCache writes - and the re-renders they trigger - to 2/sec.
 */
let tradePriceThrottle: ThrottleFuncSetup | null = null;

/**
 * Market Data Subscribe Trade Subscription Service
 */
export class MDSTSubscriptionService extends BaseSymbolSubscriptionService<SymbolSubscriptionRequestType> {
  private static instance: MDSTSubscriptionService | undefined;

  // Make the constructor private, to have a single instance
  // eslint-disable-next-line no-useless-constructor
  private constructor() {
    super();
  }

  static getInstance(): MDSTSubscriptionService {
    this.instance ??= new MDSTSubscriptionService();
    return this.instance;
  }

  /** Drop any pending throttled write so a queued update cannot land after logout. */
  public static resetInstance(): void {
    tradePriceThrottle?.cancel();
    tradePriceThrottle = null;
    super.resetInstance();
  }

  public handleResponseEvent = (event: WebSocketEventData<any>): void => {
    if (event.data.messageSubType !== GatewayResponseSubType.Trade) {
      return;
    }
    const data = event.data ?? {};

    let stockExchangeCode: StockExchangeCodeType | undefined;
    let parsedData;

    try {
      parsedData = measurePerf('mdst.parseGatewayData', undefined, () => parseGatewayData(data.arrayData));
      stockExchangeCode = this.findRequestObjectBy(parsedData);
    } catch (error) {
      configLib.store.dispatch(parsingResponseFailed(`${error}`));
      return;
    }

    const parsedRows = parsedData;
    parsedData = {
      raw: data,
      final: measurePerf(
        'mdst.dataArrayToMap',
        { rows: parsedRows?.length ?? 0, stockExchangeCode },
        () => dataArrayToMap(parsedRows),
      ),
    };

    if (isPerfMetricsEnabled()) {
      SubscriptionTimingTracker.markPrice(Object.keys(parsedData.final ?? {}));
    }

    this.updateTradePriceCache(parsedData.final as TradeDataCache, stockExchangeCode);
  };

  private updateTradePriceCache(
    parsedData: TradeDataCache,
    stockExchangeCode?: StockExchangeCodeType,
  ): void {
    // Metrics metadata must cost nothing, and must never assume a shape the response may not have
    const symbolCount = isPerfMetricsEnabled() ? Object.keys(parsedData ?? {}).length : 0;

    if (tradePriceThrottle) {
      const startedAt = performance.now();
      tradePriceThrottle.setData(parsedData);
      tradePriceThrottle.call();
      recordPerfMetric('mdst.throttleMergeAndCall', performance.now() - startedAt, { symbolCount });
      return;
    }

    const startedAt = performance.now();
    tradePriceThrottle = throttleFunctionSetupForSymbolDataUpdates(
      THROTTLE_NAME,
      parsedData,
      TRADE_PRICE_CACHE_UPDATE_THROTTLE_MS,
      THROTTLE_OPTIONS,
      data => updateTradePriceCacheFromTrade(data, stockExchangeCode),
    );
    recordPerfMetric('mdst.throttleCreateAndFirstCall', performance.now() - startedAt, { symbolCount });
  }

  private findRequestObjectBy(parsedData: ReturnType<typeof parseGatewayData>): StockExchangeCodeType | undefined {
    const symbol: string = parsedData ? parsedData[0]?.symbolonly : undefined; // All symbols already have same stock exchange code
    const traceIdentifierRequest = this.subscripitonRequests.find(item => item.symbols.includes(symbol));
    if (!traceIdentifierRequest) {
      logger.warn(`${LOG_PREFIX} request not found by the given symbol! symbol:`, symbol);
      return undefined;
    }

    return traceIdentifierRequest.stockExchangeCode;
  }

  protected shouldHandleResponse(event: WebSocketEventData<any>): boolean {
    return event.data?.messageSubType === GatewayResponseSubType.Trade;
  }

  protected getResponseType(): GatewayResponseType {
    return GatewayResponseType.MarketData;
  }

  protected getSubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.MarketDataSubscribeTrade;
  }

  protected getUnsubscriptionRequestType(): GatewayRequestType {
    return GatewayRequestType.MarketDataUnsubscribeTrade;
  }
}
